import React, { useState, useEffect, useCallback } from "react";
import { CHAT_REACTIONS, CHAT_REACTION_LABELS, type ChatReaction } from "~shared/chatExperienceSurvey";
import { ChatSurveyRating } from "./ChatSurveyRating";
import Auth from "../Auth";
import { cn } from "src/lib/cn";
import { SudoButton } from "src/sudokit/components/SudoButton";
import { Text } from "src/sudokit/components/Text";
import MenuSelect from "src/sudokit/components/SudoSelect/MenuSelect";
import { Input } from "src/sudokit/components/Input";
import { Spinner } from "src/sudokit/components/Spinner";
import { AgenticChatDetailModal } from "./AgenticChats";
import { SudoSegmentedControl } from "src/sudokit/components/SudoSegmentedControl";
import ChatSurveyTrends, { type TrendsDrill } from "./ChatSurveyTrends";

type SurveyView = "responses" | "trends";

function viewFromUrl(): SurveyView {
  return new URLSearchParams(window.location.search).get("view") === "trends" ? "trends" : "responses";
}

type ViewingChat = {
  userId: string;
  chatId: string;
  focusMessageId?: string | null;
};

interface CharStats {
  total: number;
  avg: number;
  max: number;
  min: number;
}

interface SurveyItem {
  id: string;
  userId: string;
  chatId: string;
  projectId: string;
  rating: number | null;
  reaction?: ChatReaction | null;
  skipped: boolean;
  feedbackText: string | null;
  assistantReplyCount: number | null;
  mode: string | null;
  modelKey: string | null;
  provider: string | null;
  subprovider: string | null;
  totalCostUsd: number | null;
  messageCount: number | null;
  lastMessageId: string | null;
  chapterCount: number | null;
  characterCount: number | null;
  worldbuildingCount: number | null;
  outlinePresent: boolean | null;
  characterChars: CharStats | null;
  worldbuildingChars: CharStats | null;
  createdAt: string;
}

interface SurveyStats {
  total: number;
  ratedCount: number;
  skippedCount: number;
  withTextCount: number;
  reactionCounts?: Record<ChatReaction, number>;
}

type SurveySettings = { enabled: boolean; intervalReplies: number };

const LIMIT = 50;

function Dash() {
  return <span className="text-app-grey-2">-</span>;
}

function truncateId(id: string, keep = 14) {
  return id.length > keep ? `${id.slice(0, keep)}…` : id;
}

function formatCharStats(stats: CharStats | null) {
  if (!stats) return null;
  return `${stats.total.toLocaleString()} total · ${stats.avg.toLocaleString()} avg · ${stats.max.toLocaleString()} max · ${stats.min.toLocaleString()} min`;
}

function SurveyDetailPanel({
  survey,
  onOpenThread,
  onOpenMessage,
}: {
  survey: SurveyItem;
  onOpenThread: () => void;
  onOpenMessage: () => void;
}) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-5 px-4 py-4 bg-app-menu-color-hover/40 border-t border-app-menu-color-hover">
      <div className="space-y-2">
        <Text className="text-[10px] uppercase tracking-wider text-app-grey-2">Identifiers</Text>
        <div className="space-y-1.5">
          <div className="flex items-center gap-2 min-w-0">
            <Text className="text-xs text-app-grey-2 shrink-0">Thread</Text>
            <button
              type="button"
              onClick={onOpenThread}
              className="text-xs font-mono text-app-primary-cta-high-contrast hover:brightness-110 hover:underline transition-all truncate text-left !bg-transparent !p-0"
              title={`Open thread ${survey.chatId}`}
            >
              {survey.chatId}
            </button>
          </div>
          <div className="flex items-center gap-2 min-w-0">
            <Text className="text-xs text-app-grey-2 shrink-0">Last msg</Text>
            {survey.lastMessageId ? (
              <button
                type="button"
                onClick={onOpenMessage}
                className="text-xs font-mono text-app-primary-cta-high-contrast hover:brightness-110 hover:underline transition-all truncate text-left !bg-transparent !p-0"
                title={`Open message ${survey.lastMessageId}`}
              >
                {survey.lastMessageId}
              </button>
            ) : (
              <Dash />
            )}
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <Text className="text-[10px] uppercase tracking-wider text-app-grey-2">Characters</Text>
        <Text className="text-sm text-app-grey-0">
          {survey.characterCount != null ? `${survey.characterCount} entries` : <Dash />}
        </Text>
        <Text className="text-xs text-app-grey-1">
          {formatCharStats(survey.characterChars) ?? <Dash />}
        </Text>
      </div>

      <div className="space-y-2">
        <Text className="text-[10px] uppercase tracking-wider text-app-grey-2">Worldbuilding</Text>
        <Text className="text-sm text-app-grey-0">
          {survey.worldbuildingCount != null ? `${survey.worldbuildingCount} entries` : <Dash />}
        </Text>
        <Text className="text-xs text-app-grey-1">
          {formatCharStats(survey.worldbuildingChars) ?? <Dash />}
        </Text>
      </div>
    </div>
  );
}

export default function ChatExperienceSurvey() {
  const [surveys, setSurveys] = useState<SurveyItem[]>([]);
  const [stats, setStats] = useState<SurveyStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [ratingFilter, setRatingFilter] = useState<number | ChatReaction | null>(null);
  const [skippedFilter, setSkippedFilter] = useState<"all" | "rated" | "skipped">("all");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [viewingChat, setViewingChat] = useState<ViewingChat | null>(null);
  const [settings, setSettings] = useState<SurveySettings | null>(null);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [view, setView] = useState<SurveyView>(viewFromUrl);
  const [drill, setDrill] = useState<TrendsDrill | null>(null);
  const [trendsOpened, setTrendsOpened] = useState(view === "trends");

  const changeView = (next: SurveyView) => {
    setView(next);
    if (next === "trends") setTrendsOpened(true);
    const url = new URL(window.location.href);
    if (next === "trends") url.searchParams.set("view", "trends");
    else url.searchParams.delete("view");
    window.history.replaceState(window.history.state, "", url);
  };

  const buildParams = useCallback(() => {
    const params: Record<string, unknown> = {};
    if (drill) params.ids = drill.ids;
    if (typeof ratingFilter === "number") params.rating = ratingFilter;
    else if (ratingFilter !== null) params.reaction = ratingFilter;
    if (skippedFilter === "skipped") params.skipped = true;
    if (skippedFilter === "rated") params.skipped = false;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (search.trim()) params.search = search.trim();
    return params;
  }, [ratingFilter, skippedFilter, startDate, endDate, search, drill]);

  useEffect(() => {
    if (view !== "responses") return;
    let active = true;
    setLoading(true);
    setStats(null);
    const fetchSurveys = async () => {
      try {
        const params = { ...buildParams(), page, limit: LIMIT };
        const result = await Auth.fetchWithIdToken("/api/admin/chat-experience-survey/list", params);
        if (active && result.success) {
          setSurveys(result.surveys);
          setStats(result.stats);
          setTotal(result.pagination.total);
        }
      } catch (error) {
        if (active) console.error("Error fetching surveys:", error);
      } finally {
        if (active) setLoading(false);
      }
    };
    void fetchSurveys();
    return () => { active = false; };
  }, [page, buildParams, view]);

  useEffect(() => {
    void Auth.fetchWithIdToken("/api/admin/chat-experience-survey/settings", {})
      .then((result) => {
        if (result.success) setSettings(result.settings);
      })
      .catch((error) => console.error("Error fetching survey settings:", error));
  }, []);

  const saveSettings = async () => {
    if (!settings) return;
    setSettingsSaving(true);
    setSettingsSaved(false);
    try {
      const result = await Auth.fetchWithIdToken(
        "/api/admin/chat-experience-survey/settings/update",
        settings,
      );
      if (result.success) {
        setSettings(result.settings);
        setSettingsSaved(true);
      }
    } catch (error) {
      console.error("Error updating survey settings:", error);
    } finally {
      setSettingsSaving(false);
    }
  };

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleExport = async (format: "csv" | "json") => {
    try {
      const params = { ...buildParams(), format };
      if (format === "csv") {
        const blob = await Auth.fetchFileWithIdToken("/api/admin/chat-experience-survey/export", params);
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "chat-experience-surveys.csv";
        a.click();
        URL.revokeObjectURL(url);
        return;
      }

      const result = await Auth.fetchWithIdToken("/api/admin/chat-experience-survey/export", params);
      const blob = new Blob([JSON.stringify(result, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "chat-experience-surveys.json";
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));
  const headers = ["", "Date", "Rating", "Feedback", "User", "Mode", "Provider", "Subprovider", "Cost", "Msgs", "Chapters", "Story bible"];

  return (
    <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pt-6">
      <div className="sm:flex sm:items-center mb-8">
        <div className="sm:flex-auto gap-1 flex-col flex">
          <Text className="text-3xl font-semibold bg-gradient-to-r from-app-grey-0 to-app-grey-1 bg-clip-text text-transparent">
            Chat Experience Survey
          </Text>
          <Text className="text-base text-app-grey-1">
            Review and export &ldquo;How was your chat experience?&rdquo; survey responses
          </Text>
        </div>
      </div>

      {settings && (
        <section className="mb-6 flex flex-wrap items-end gap-5 rounded-xl border border-solid border-app-menu-color-hover bg-app-menu-color px-5 py-4">
          <label className="flex min-h-9 items-center gap-3 text-sm text-app-grey-0">
            <input
              aria-label="Enable chat experience survey"
              type="checkbox"
              checked={settings.enabled}
              onChange={(event) => {
                setSettingsSaved(false);
                setSettings((current) => current && ({ ...current, enabled: event.target.checked }));
              }}
              className="h-4 w-4 accent-app-primary"
            />
            Enable rating component globally
          </label>
          <label className="flex flex-col gap-1 text-sm text-app-grey-1">
            Assistant turns before showing
            <Input
              aria-label="Assistant turns before showing"
              type="number"
              mode="default"
              min={1}
              max={100}
              value={settings.intervalReplies}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setSettingsSaved(false);
                setSettings((current) => current && ({
                  ...current,
                  intervalReplies: Math.min(100, Math.max(1, Number(event.target.value) || 1)),
                }));
              }}
              className="w-28 text-sm"
            />
          </label>
          <SudoButton
            type="button"
            variant="primary"
            size="small"
            disabled={settingsSaving}
            onClick={saveSettings}
          >
            {settingsSaving ? "Saving..." : "Save settings"}
          </SudoButton>
          {settingsSaved && <Text className="text-sm text-app-grey-1">Saved</Text>}
        </section>
      )}

      <SudoSegmentedControl
        aria-label="Survey view"
        className="mb-6"
        value={view}
        onChange={(value) => changeView(value as SurveyView)}
        options={[{ value: "responses", label: "Responses" }, { value: "trends", label: "Trends" }]}
      />

      {trendsOpened && (
        // Stays mounted after the first visit so its filters survive a trip to Responses.
        <div hidden={view !== "trends"}>
          <ChatSurveyTrends
            onOpenResponses={(next) => {
              // Show exactly the selected answers, not whatever the Responses filters were.
              setRatingFilter(null);
              setSkippedFilter("all");
              setSearch("");
              setSearchInput("");
              setStartDate("");
              setEndDate("");
              setDrill(next);
              setPage(1);
              changeView("responses");
            }}
          />
        </div>
      )}

      {view === "responses" && (
      <>
      {drill && (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-solid border-app-menu-color-hover bg-app-menu-color px-4 py-2.5">
          <Text className="text-sm text-app-grey-1">
            Opened from Trends: <span className="font-semibold text-app-grey-0">{drill.ids.length.toLocaleString()} responses</span> · {drill.label}
          </Text>
          <SudoButton type="button" variant="secondaryGrey" size="small" onClick={() => { setDrill(null); setPage(1); }}>
            Clear
          </SudoButton>
          <SudoButton type="button" variant="secondaryGrey" size="small" onClick={() => changeView("trends")}>
            Back to Trends
          </SudoButton>
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { label: "Total", value: String(stats.total) },
            { label: "Rated", value: String(stats.ratedCount) },
            { label: "Skipped", value: String(stats.skippedCount) },
            { label: "With comment", value: String(stats.withTextCount) },
          ].map((s) => (
            <div key={s.label} className="bg-app-menu-color rounded-xl border border-solid border-app-menu-color-hover px-4 py-3">
              <Text className="text-xs uppercase tracking-wider text-app-grey-2">{s.label}</Text>
              <Text className="text-2xl font-semibold text-app-grey-0">{s.value}</Text>
            </div>
          ))}
        </div>
      )}

      {stats && (
        <section aria-label="Reaction breakdown" className="mb-6">
          <div className="grid grid-cols-3 gap-3">
            {CHAT_REACTIONS.map((reaction) => {
              const count = stats.reactionCounts?.[reaction] ?? 0;
              const total = CHAT_REACTIONS.reduce((sum, key) => sum + (stats.reactionCounts?.[key] ?? 0), 0);
              return (
                <div key={reaction} className="bg-app-menu-color rounded-xl border border-solid border-app-menu-color-hover px-4 py-3">
                  <div className="text-sm text-app-grey-1"><ChatSurveyRating rating={null} reaction={reaction} /></div>
                  <Text className="text-2xl font-semibold text-app-grey-0">{total > 0 ? `${Math.round(count / total * 100)}%` : "—"}</Text>
                  <Text className="text-xs text-app-grey-2">{count.toLocaleString()} {count === 1 ? "response" : "responses"}</Text>
                </div>
              );
            })}
          </div>
          <Text className="mt-2 text-xs text-app-grey-2">Share of reactions matching the current filters. Excludes legacy stars and skipped surveys.</Text>
        </section>
      )}

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <MenuSelect
            label="Filter by rating"
            value={ratingFilter === null ? "all" : String(ratingFilter)}
            onChange={(value: string) => {
              setRatingFilter(value === "all" ? null : CHAT_REACTIONS.includes(value as ChatReaction) ? value as ChatReaction : Number(value));
              setPage(1);
            }}
            options={[
              { value: "all", label: "All Ratings" },
              ...CHAT_REACTIONS.map((value) => ({ value, label: CHAT_REACTION_LABELS[value] })),
              { value: "5", label: "5 stars" },
              { value: "4", label: "4 stars" },
              { value: "3", label: "3 stars" },
              { value: "2", label: "2 stars" },
              { value: "1", label: "1 star" },
            ]}
            placeholder="Filter by rating"
          />

          <MenuSelect
            label="Response type"
            value={skippedFilter}
            onChange={(value: string) => {
              setSkippedFilter(value as "all" | "rated" | "skipped");
              setPage(1);
            }}
            options={[
              { value: "all", label: "All responses" },
              { value: "rated", label: "Rated only" },
              { value: "skipped", label: "Skipped only" },
            ]}
            placeholder="Response type"
          />

          <form
            className="flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setSearch(searchInput);
              setPage(1);
            }}
          >
            <Input
              type="text"
              mode="default"
              value={searchInput}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
              placeholder="Search user / chat / project / text"
              className="w-64 text-sm"
            />
            <SudoButton type="submit" variant="secondaryGrey" size="small">
              Search
            </SudoButton>
          </form>

          <div className="flex items-center gap-2">
            <Text className="text-sm text-app-grey-1">From</Text>
            <Input
              type="date"
              mode="default"
              value={startDate}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setStartDate(e.target.value); setPage(1); }}
              className="w-36 text-sm"
            />
          </div>

          <div className="flex items-center gap-2">
            <Text className="text-sm text-app-grey-1">To</Text>
            <Input
              type="date"
              mode="default"
              value={endDate}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setEndDate(e.target.value); setPage(1); }}
              className="w-36 text-sm"
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <SudoButton variant="secondaryGrey" size="small" onClick={() => handleExport("csv")}>
            Export CSV
          </SudoButton>
          <SudoButton variant="secondaryGrey" size="small" onClick={() => handleExport("json")}>
            Export JSON
          </SudoButton>
        </div>
      </div>

      <div className="mt-4">
        {loading ? (
          <div className="flex items-center justify-center py-32">
            <div className="text-center">
              <Spinner size="lg" className="mx-auto mb-4" />
              <Text className="text-sm font-medium text-app-grey-1">Loading surveys...</Text>
            </div>
          </div>
        ) : surveys.length === 0 ? (
          <div className="text-center py-12 px-4 bg-app-menu-color rounded-xl border border-app-menu-color-hover shadow-card animate-in fade-in">
            <Text className="text-app-grey-1">No surveys found matching the current filters.</Text>
          </div>
        ) : (
          <>
            <div className="rounded-xl bg-app-menu-color border border-app-menu-color-hover shadow-card overflow-auto">
              <table className="min-w-full divide-y divide-app-menu-color-hover">
                <thead className="bg-app-menu-color h-9">
                  <tr className="border-b border-app-menu-color-hover">
                    {headers.map((h) => (
                      <th
                        key={h || "expand"}
                        scope="col"
                        className="px-4 py-4 text-left text-xs font-semibold text-app-grey-1 uppercase tracking-wider"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {surveys.map((s, index) => {
                    const expanded = expandedIds.has(s.id);
                    return (
                      <React.Fragment key={s.id}>
                        <tr
                          className="hover:bg-app-menu-color-hover transition-colors animate-in fade-in slide-in-from-bottom-2 duration-300"
                          style={{ animationDelay: `${Math.min(index * 30, 500)}ms`, animationFillMode: "both" }}
                        >
                          <td className="px-2 py-4 whitespace-nowrap">
                            <button
                              type="button"
                              aria-label={expanded ? "Collapse survey details" : "Expand survey details"}
                              aria-expanded={expanded}
                              onClick={() => toggleExpanded(s.id)}
                              className="inline-flex items-center justify-center w-7 h-7 rounded-md !bg-transparent !text-app-grey-1 hover:!text-app-grey-0 transition-colors"
                            >
                              <span className={cn("!text-base leading-none transition-transform", expanded && "rotate-90")}>▸</span>
                            </button>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-app-grey-0">
                            {new Date(s.createdAt).toLocaleDateString()}{" "}
                            {new Date(s.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            {s.skipped ? (
                              <span className="inline-flex items-center px-3 py-1 rounded-lg text-xs font-semibold bg-app-grey-3/30 text-app-grey-1">
                                Skipped
                              </span>
                            ) : s.rating != null || s.reaction ? (
                              <ChatSurveyRating rating={s.rating} reaction={s.reaction} />
                            ) : (
                              <Dash />
                            )}
                          </td>
                          <td className="px-4 py-4 text-sm min-w-[320px] max-w-[480px] align-top">
                            {s.feedbackText ? (
                              <span className="text-app-grey-0 whitespace-normal break-words">{s.feedbackText}</span>
                            ) : (
                              <span className="text-app-grey-2 italic">No text</span>
                            )}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <a
                              href={`/admin/user/${s.userId}`}
                              className="text-xs font-mono text-app-primary-cta-high-contrast hover:brightness-110 transition-all"
                              title={s.userId}
                            >
                              {truncateId(s.userId, 12)}
                            </a>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            {s.mode ? (
                              <span
                                className={cn(
                                  "inline-flex items-center px-3 py-1 rounded-lg text-xs font-semibold",
                                  s.mode === "write" ? "bg-blue-500/10 text-blue-500" : "bg-purple-500/10 text-purple-500",
                                )}
                              >
                                {s.mode.charAt(0).toUpperCase() + s.mode.slice(1)}
                              </span>
                            ) : (
                              <Dash />
                            )}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-app-grey-0">
                            {s.provider ?? <Dash />}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-app-grey-1">
                            {s.subprovider ?? <Dash />}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-mono text-app-grey-0">
                            {s.totalCostUsd != null ? `$${s.totalCostUsd.toFixed(4)}` : <Dash />}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-app-grey-0">
                            {s.messageCount ?? <Dash />}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-app-grey-0">
                            {s.chapterCount ?? <Dash />}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-app-grey-1">
                            {s.characterCount != null || s.worldbuildingCount != null ? (
                              <span title="characters / worldbuilding / outline">
                                {s.characterCount ?? 0}c · {s.worldbuildingCount ?? 0}w
                                {s.outlinePresent ? " · outline" : ""}
                              </span>
                            ) : (
                              <Dash />
                            )}
                          </td>
                        </tr>
                        {expanded && (
                          <tr>
                            <td colSpan={headers.length} className="p-0">
                              <SurveyDetailPanel
                                survey={s}
                                onOpenThread={() =>
                                  setViewingChat({
                                    userId: s.userId,
                                    chatId: s.chatId,
                                  })
                                }
                                onOpenMessage={() =>
                                  setViewingChat({
                                    userId: s.userId,
                                    chatId: s.chatId,
                                    focusMessageId: s.lastMessageId,
                                  })
                                }
                              />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between mt-4 pb-6">
              <Text className="text-sm text-app-grey-1">
                Showing {((page - 1) * LIMIT) + 1}–{Math.min(page * LIMIT, total)} of {total}
              </Text>
              <div className="flex items-center gap-2">
                <SudoButton
                  variant="secondaryGrey"
                  size="small"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  Previous
                </SudoButton>
                <Text className="text-sm text-app-grey-0 px-2">
                  Page {page} of {totalPages}
                </Text>
                <SudoButton
                  variant="secondaryGrey"
                  size="small"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                >
                  Next
                </SudoButton>
              </div>
            </div>
          </>
        )}
      </div>

      </>
      )}

      {viewingChat && (
        <AgenticChatDetailModal
          userId={viewingChat.userId}
          chatId={viewingChat.chatId}
          focusMessageId={viewingChat.focusMessageId}
          onClose={() => setViewingChat(null)}
        />
      )}
    </div>
  );
}
